import { OrgFullDetail } from "@garther/interfaces";
import { ConfirmDialog, CreateCard, InfoCard } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { MapPin, Trash } from "lucide-react";
import { useState } from "react";

import { useDeleteOrgLocation } from "@/features/org/hooks/use-org-query";

interface OrgLocationProps {
  org: OrgFullDetail;
}

export function OrgLocation({ org }: OrgLocationProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const router = useRouter();
  const [deleteLocationId, setDeleteLocationId] = useState<string | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const deleteMutation = useDeleteOrgLocation({
    onSuccess: () => {
      toast.success(t("org.location.deleteSuccess"));
      setIsDeleteConfirmOpen(false);
      setDeleteLocationId(null);
      router.invalidate();
    }
  });

  const handleDeleteClick = (e: React.MouseEvent, locationId: string) => {
    e.stopPropagation();
    setDeleteLocationId(locationId);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteLocationId) {
      deleteMutation.mutate({ orgId: org.id, locationId: deleteLocationId });
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteLocationId(null);
  };

  if (org.locations.length === 0) {
    return (
      <div className="space-y-6">
        <div className="w-full py-12 border border-border rounded-lg bg-background-secondary/30">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto bg-background-dark-secondary/10 rounded-full flex items-center justify-center">
                <MapPin className="w-8 h-8 text-text-secondary" strokeWidth={1.5} />
              </div>
            </div>
            <div className="space-y-2 mb-6">
              <p className="text-lg font-medium text-text-primary">{t("org.location.empty.title")}</p>
              <p className="text-sm text-text-secondary max-w-md">{t("org.location.empty.description")}</p>
            </div>
            <CreateCard
              className="min-h-[180px] max-w-xs"
              title={t("org.location.empty.createButton")}
              subtitle={t("org.location.empty.createSubtitle")}
              onClick={() => {
                navigate({
                  to: "/org/$orgId/location/create",
                  params: { orgId: org.id }
                });
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
        {org.locations.map((location) => (
          <InfoCard
            key={location.id}
            className="min-h-[180px] hover:bg-background-highlight"
            icon={MapPin}
            title={t("org.location.address")}
            subtitle={location.name}
            description={location.address}
            footer={
              <div>
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Click to view on Google Maps
                </div>
              </div>
            }
            action={{
              icon: Trash,
              label: "Delete location",
              onClick: (e) => handleDeleteClick(e, location.id),
              variant: "danger",
              disabled: deleteMutation.isPending
            }}
            onClick={() => {
              window.open(`https://www.google.com/maps?q=${location.lat},${location.long}`, "_blank");
            }}
          />
        ))}

        <CreateCard
          className="min-h-[180px]"
          title="Create Location"
          subtitle="Add a new location"
          onClick={() => {
            navigate({
              to: "/org/$orgId/location/create",
              params: { orgId: org.id }
            });
          }}
        />
      </div>

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("org.location.deleteConfirmTitle") || "Xác nhận xóa địa điểm"}
        message={
          t("org.location.deleteConfirmMessage") ||
          "Bạn có chắc chắn muốn xóa địa điểm này? Hành động này không thể hoàn tác."
        }
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
